import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';
import { Btn, Input, Alert } from '../components/ui';
import { redirectByRole } from './Login';

function RuleItem({ met, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <span style={{
        width: '18px', height: '18px', borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '11px', fontWeight: 700,
        background: met ? 'var(--color-success)' : 'var(--color-border)',
        color: met ? '#fff' : 'var(--color-text-muted)',
        flexShrink: 0, transition: 'background 0.2s',
      }}>
        {met ? '✓' : ''}
      </span>
      <span style={{ fontSize: '13px', color: met ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
        {text}
      </span>
    </div>
  );
}

export default function ResetPassword() {
  const { user, refreshUser }    = useAuth();
  const navigate                 = useNavigate();
  const [newPwd,  setNew]        = useState('');
  const [confPwd, setConf]       = useState('');
  const [error,   setErr]        = useState('');
  const [loading, setLoad]       = useState(false);

  const rules = {
    length:  newPwd.length >= 8,
    number:  /\d/.test(newPwd),
    special: /[!@#$%^&*()\-_=+\[\]{};':"\\|,.<>/?]/.test(newPwd),
    match:   newPwd.length > 0 && newPwd === confPwd,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rules.length)  { setErr('Password must be at least 8 characters');       return; }
    if (!rules.number)  { setErr('Password must contain at least one number');     return; }
    if (!rules.special) { setErr('Password must contain at least one special character'); return; }
    if (!rules.match)   { setErr('Passwords do not match');                        return; }
    setErr(''); setLoad(true);
    try {
      const res = await authApi.resetPassword(newPwd, confPwd);
      if (res.data.success) {
        await refreshUser();
        redirectByRole(res.data.data.role, navigate);
      } else {
        setErr(res.data.message);
      }
    } catch (err) {
      setErr(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoad(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}>
          <div style={S.logoMark}>K</div>
          <h2 style={S.title}>Set your password</h2>
          <p style={S.subtitle}>
            Welcome{user?.name ? `, ${user.name}` : ''}! You must set a new password before continuing.
          </p>
        </div>
        <div style={S.rules}>
          <p style={S.rulesTitle}>Your password must have:</p>
          <RuleItem met={rules.length}  text="At least 8 characters"            />
          <RuleItem met={rules.number}  text="At least one number"               />
          <RuleItem met={rules.special} text="At least one special character"    />
          <RuleItem met={rules.match}   text="Both passwords match"              />
        </div>
        <form onSubmit={handleSubmit} style={S.form}>
          <Alert type="error" message={error} />
          <Input
            label="New Password"
            type="password"
            placeholder="Create a strong password"
            value={newPwd}
            onChange={e => setNew(e.target.value)}
            autoComplete="new-password"
          />
          <Input
            label="Confirm Password"
            type="password"
            placeholder="Re-enter your password"
            value={confPwd}
            onChange={e => setConf(e.target.value)}
            autoComplete="new-password"
          />
          <Btn type="submit" fullWidth loading={loading} size="lg">
            Set Password &amp; Continue
          </Btn>
        </form>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px',
    background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)',
  },
  card: {
    background: '#fff', borderRadius: '20px', padding: '36px 32px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  header:    { textAlign: 'center', marginBottom: '24px' },
  logoMark: {
    width: '48px', height: '48px', borderRadius: '14px',
    background: 'var(--color-primary)', color: '#fff',
    fontSize: '24px', fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '12px',
  },
  title:     { fontSize: '20px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '8px' },
  subtitle:  { fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.5 },
  rules: {
    background: 'var(--color-bg)', borderRadius: 'var(--radius-md)',
    padding: '14px 16px', marginBottom: '20px',
  },
  rulesTitle: {
    fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)',
    marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
};
